import { useCallback, useEffect, useRef } from 'react';

/**
 * Fire callback when browser window is focused
 * @param callback called when window is focused outside of the interval timeout
 * @param interval interval between last focus callback
 * @returns toggle window focus event on/off
 */
export function useWindowFocus(callback: () => void, interval = 10) {
  const shouldRunRef = useRef(true);
  const lastCheckRef = useRef<number>();

  const resumeFocusEvent = useCallback((shouldResume: boolean) => {
    // reset check if changing value
    if (shouldRunRef.current !== shouldResume) {
      lastCheckRef.current = undefined;
    }
    shouldRunRef.current = shouldResume;
  }, []);

  useEffect(() => {
    let isFirstCheck = true;
    const focus = () => {
      if (!shouldRunRef.current) {
        return;
      }
      const now = Date.now();
      if (typeof lastCheckRef.current === 'undefined') {
        lastCheckRef.current = now;
      }
      const diffSec = Math.abs(now - lastCheckRef.current) / 1000;
      if (isFirstCheck || diffSec > interval) {
        isFirstCheck = false;
        lastCheckRef.current = now;
        callback();
      }
    };
    focus();
    window.addEventListener('focus', focus);
    return () => {
      lastCheckRef.current = undefined;
      window.removeEventListener('focus', focus);
    };
  }, [interval, callback]);

  return resumeFocusEvent;
}
